import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { activePlayerIdAtom, PlayerMode, useAudioPlayer } from '@/hooks/useAudioPlayer';
import Loading from '@/images/Loading.svg';
import PlayPreview from '@/images/PlayPreview.svg';
import StopPreview from '@/images/StopPreview.svg';
import { useAnalytics } from '@/services/analytics';
import { logger } from '@/services/logger';
import { DEFAULT_VOICE_CONFIG, ttsGenerationErrors } from '@/state/presenters';
import { IVoice, Providers, VoiceAccess } from '@d-id/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { MutableRefObject, useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

const HeaderButton = styled.button`
    border: none;
    box-sizing: border-box;
    padding: 0;
    color: inherit;

    :disabled {
        opacity: 0.5;
    }
`;

const VoicePlayerButton = styled(HeaderButton)`
    background-color: transparent;
    cursor: pointer;
    width: 32px;
    height: 32px;
    outline: none;

    :disabled {
        cursor: default;
    }
`;

const ListenImage = styled.img`
    height: 2rem;
    width: 2rem;
`;

const PreviewImage = styled.img`
    height: 2rem;
    width: 2rem;
`;

const ImageWrapper = styled.div`
    height: 32px;
    width: 32px;
    background-color: ${({ theme }) => theme.colors.black.main + '10'};
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const voiceTexts: Record<string, string> = {
    multilingual: 'Hey, nice to meet you. What do you think of my voice?',
    english: 'Hey, nice to meet you. What do you think of my voice?',
    afrikaans: 'Haai, lekker om jou te ontmoet. Wat dink jy van my stem?',
    albanian: 'Hej, jam mirë që ju njohëm. Çfarë mendoni për zërin tim?',
    amharic: 'ሄይ፣ ስለተዋወቅን ደስ ብሎኛል። ስለ ድምፄ ምን ታስባለህ?',
    arabic: 'مرحبا انا سعيد للقائك. ما رأيك في صوتي؟',
    armenian: 'Հեյ, հաճելի է հանդիպել քեզ: Ի՞նչ կարծիքի ես իմ ձայնի մասին։',
    azerbaijani: 'Hey, sizinlə tanış olmaqdan məmnunam. Mənim səsim haqqında nə düşünürsünüz?',
    bangla: 'আপনার সাথে দেখা করতে হেই চমৎকার. আমার কন্ঠ সম্পর্কে আপনি কি মনে করেন?',
    basque: 'Aizu, pozten naiz zu ezagutzea. Zer iruditzen zaizu nire ahotsa?',
    bosnian: 'Hej, drago mi je. Šta misliš o mom glasu?',
    bulgarian: 'Хей, приятно ми е да се запознаем. Какво мислиш за гласа ми?',
    burmese: 'ဟေး တွေ့ရတာဝမ်းသာပါတယ်။ ငါ့အသံကို မင်းဘယ်လိုထင်လဲ။',
    catalan: 'Ei, un plaer conèixer-te. Què et sembla la meva veu?',
    chinese: '你好，很高兴遇到你。你觉得我的声音怎么样？',
    'chinese simplified': '你好，很高兴遇到你。你觉得我的声音怎么样？',
    'chinese traditional': '你好，很高興遇到你。你覺得我的聲音怎麼樣？',
    croatian: 'Hej, drago mi je. Što misliš o mom glasu?',
    czech: 'Ahoj, rád tě poznávám. Co si myslíš o mém hlase?',
    danish: 'Hej, rart at møde dig. Hvad synes du om min stemme?',
    dutch: 'Hallo leuk je te ontmoeten. Wat vind je van mijn stem?',
    estonian: 'Tere, meeldiv tutvuda. Mis sa mu häälest arvad?',
    filipino: 'Ikinagagalak kong makilala ka. Anong tingin mo sa boses ko?',
    finnish: 'Hei hauska tavata. Mitä mieltä olet äänestäni?',
    french: "Bonjour, enchanté de faire votre connaissance. Qu'est ce que vous pensez de ma voix?",
    galician: 'Ola, encantado de coñecerte. Que opinas da miña voz?',
    georgian: 'ჰეი, სასიამოვნოა თქვენი გაცნობა. რას ფიქრობ ჩემს ხმაზე?',
    german: 'Hey, freut mich dich kennen zu lernen. Was denkst du über meine Stimme?',
    greek: 'Ει Χαίρομαι που σε γνωρίζω. Τι γνώμη έχετε για τη φωνή μου;',
    gujarati: 'અરે, તમને મળીને આનંદ થયો. તમે મારા અવાજ વિશે શું વિચારો છો?',
    hebrew: 'היי, נעים להכיר מה אתה חושב על הקול שלי?',
    hindi: 'अहो, तुमसे मिलकर बहुत अच्छा लगा। आप मेरी आवाज के बारे में क्या सोचते हैं?',
    hungarian: 'Szia, örülök, hogy megismertelek. mit gondolsz a hangomról?',
    icelandic: 'Hæ, gaman að hitta þig. Hvað finnst þér um röddina mína?',
    indonesian: 'Hei, senang bertemu denganmu. Bagaimana pendapat Anda tentang suara saya?',
    irish: 'Hey, deas bualadh leat. Cad a cheapann tú de mo ghuth?',
    italian: 'Ehi piacere di conoscerti. cosa ne pensi della mia voce?',
    japanese: 'やあ、初めまして。私の声についてどう思いますか？',
    javanese: 'Hey, seneng ketemu kowe. Piye menurutmu suaraku?',
    kannada: 'ಹಾಯ್, ನಿಮ್ಮನ್ನು ಭೇಟಿಯಾಗಲು ಸಂತೋಷವಾಗಿದೆ. ನನ್ನ ಧ್ವನಿಯ ಬಗ್ಗೆ ನಿಮ್ಮ ಅಭಿಪ್ರಾಯವೇನು?',
    kazakh: 'Эй, танысқаныма қуаныштымын. Менің дауысым туралы не ойлайсыз?',
    khmer: 'ហេ រីករាយដែលបានជួបអ្នក។ តើអ្នកយល់យ៉ាងណាចំពោះសំឡេងរបស់ខ្ញុំ?',
    korean: '이봐 만나서 반가워. 내 목소리에 대해 어떻게 생각하세요?',
    lao: 'Hey, ດີໃຈທີ່ໄດ້ພົບເຈົ້າ. ເຈົ້າຄິດແນວໃດກັບສຽງຂອງຂ້ອຍ?',
    latvian: 'Hei, prieks iepazīties. Ko tu domā par manu balsi?',
    lithuanian: 'Labas, malonu susipažinti. ka manai apie mano balsa?',
    macedonian: 'Еј, мило ми е што те запознав. Што мислите за мојот глас?',
    malay: 'Hey, senang berkenalan. Apa pendapat anda tentang suara saya?',
    malayalam: 'ഹായ്, നിങ്ങളെ കണ്ടതിൽ സന്തോഷം. എൻ്റെ ശബ്ദത്തെക്കുറിച്ച് നിങ്ങൾക്ക് എന്തു തോന്നുന്നു?',
    maltese: "Ħej, kuntent li niltaqa' miegħek. X'taħseb tal-vuċi tiegħi?",
    marathi: 'अहो, तुम्हाला भेटून आनंद झाला. माझ्या आवाजाबद्दल तुम्हाला काय वाटते?',
    mongolian: 'Хөөе, танилцсандаа таатай байна. Миний хоолойг юу гэж бодож байна?',
    nepali: 'हे, तिमीलाई भेटेर खुशी लाग्यो। तिमीलाई मेरो आवाज कस्तो लाग्छ?',
    norwegian: 'Hei hyggelig å møte deg. Hva synes du om stemmen min?',
    pashto: 'هو، ستاسو په لیدلو خوشحاله شوم. زما د غږ په اړه څه فکر کوئ؟',
    persian: 'سلام از آشنایی با شما خوشحالم در مورد صدای من چه نظری داری؟',
    polish: 'Cześć, miło Cię poznać. Co sądzisz o moim głosie?',
    portuguese: 'Ei prazer em conhecê-lo. O que você acha da minha voz?',
    romanian: 'Salut, imi pare bine sa te cunosc. Ce crezi despre vocea mea?',
    russian: 'Привет, приятно познакомиться. Что ты думаешь о моем голосе?',
    serbian: 'Хеј, лепо је што смо се срели. Шта мислиш о мом гласу?',
    sinhala: 'හේයි, ඔබව හමුවීම සතුටක්. මගේ හඬ ගැන ඔබ සිතන්නේ කුමක්ද?',
    slovak: 'Ahoj, rád ťa spoznávam. Čo si myslíš o mojom hlase?',
    slovenian: 'Hej, lepo te je spoznati. Kaj misliš o mojem glasu?',
    somali: 'Haye, waan ku faraxsanahay inaan kula kulmo. Maxaad u malayn codkayga?',
    spanish: 'Oye un placer conocerte. ¿Qué piensas de mi voz?',
    sundanese: 'Héy, wilujeng tepang. Kumaha saur anjeun sora kuring?',
    swahili: 'Hey, nimefurahi kukutana nawe. Una maoni gani kuhusu sauti yangu?',
    swedish: 'Hej trevligt att träffas. Vad tycker du om min röst?',
    tamil: 'ஐயா, உங்களை சந்தித்ததில் மகிழ்ச்சி. என் குரலைப் பற்றி நீங்கள் என்ன நினைக்கிறீர்கள்?',
    telugu: 'హే, మిమ్మల్ని కలవడం ఆనందంగా ఉంది. నా వాయిస్ గురించి మీరు ఏమనుకుంటున్నారు?',
    thai: 'เฮ้ยินดีที่ได้รู้จัก. คุณคิดอย่างไรกับเสียงของฉัน?',
    turkish: 'Hey tanıştığımıza memnun oldum. Sesim hakkında ne düşünüyorsun?',
    ukrianian: 'Гей, радий познайомитися. Що ти думаєш про мій голос?',
    urdu: 'اے. تم سے ملکر خوشی ہوئی. میری آواز کے بارے میں کیا خیال ہے؟',
    uzbek: "Hey, tanishganimdan xursandman. Mening ovozim haqida nima deb o'ylaysiz?",
    vietnamese: 'Này, rất vui được gặp bạn. Bạn nghĩ gì về giọng nói của tôi?',
    welsh: 'Hei, braf cwrdd â chi. Beth yw eich barn am fy llais?',
    zulu: 'Sawubona, ngiyajabula ukukwazi. Ucabangani ngezwi lami?',
};

interface Props {
    voice: IVoice;
    pauseCallbackRef: MutableRefObject<VoidFunction | null>;
}

export const RowVoicePlayer = observer(({ voice, pauseCallbackRef }: Props) => {
    const mixpanel = useAnalytics();

    const activePlayerId = useRecoilValue(activePlayerIdAtom);
    const voiceLanguage = voice.language.split(' ')[0].toLowerCase();
    const playerId = `${voice.id}-${voiceLanguage}`;

    const text = voiceTexts[voiceLanguage] || undefined;

    const isSupportedProvider = voice.provider !== Providers.Amazon;
    const isExternalPrivate = voice.access === VoiceAccess.ExternalPrivate;

    const listenDisabled = useMemo(
        () => !text || isExternalPrivate || !isSupportedProvider,
        [isSupportedProvider, activePlayerId, playerId]
    );

    const voiceConfig = DEFAULT_VOICE_CONFIG;
    const { playerMode, error, play, pause } = useAudioPlayer({
        voice,
        text: text || '',
        voiceConfig,
        language: voice.language,
        playerId,
        useVoicePreviewUrl: true,
    });

    useEffect(() => {
        if ([Providers.Google, Providers.Elevenlabs].includes(voice.provider)) {
            pauseCallbackRef.current = pause as any;
        }
    }, [pause]);

    const onPlay = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();

        pauseCallbackRef.current?.();

        mixpanel?.track('tts-listen', {
            from: 'voice-panel',
            event: 'play',
            voice,
            style: voiceConfig.style,
            textLength: text?.length,
        });

        if (voice) {
            play({ useIndicators: true, volume: 1, force: true });

            if (![Providers.Google, Providers.Elevenlabs].includes(voice.provider)) {
                pauseCallbackRef.current = pause as any;
            }
        }
    };

    const onPause = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        pause?.();
    };

    const tooltipText = useMemo(() => {
        if (!isSupportedProvider) {
            return "Preview isn't available for this voice";
        }
        return 'Listen';
    }, [isSupportedProvider, listenDisabled]);

    useEffect(() => pause, [pause]);

    useEffect(() => {
        if (error) {
            const errorDetails = ttsGenerationErrors[error];
            mixpanel?.track('tts-listen', {
                event: 'error',
                error,
                textLength: text?.length,
                voice,
                style: voiceConfig.style,
            });
            if (error === 'unknown') {
                logger.error('Error playing TTS', { error: errorDetails, text, voice, style: voiceConfig.style });
            }
        }
    }, [error]);

    if (playerMode === PlayerMode.Loading && playerId === activePlayerId) {
        return (
            <CustomTooltip title="" placement="top" cursor="pointer">
                <VoicePlayerButton disabled>
                    <ListenImage src={Loading} />
                </VoicePlayerButton>
            </CustomTooltip>
        );
    }

    if (playerMode === PlayerMode.Playing && playerId === activePlayerId) {
        return (
            <CustomTooltip title="" placement="top" disabled={!voice} cursor="pointer" onClick={onPause}>
                <VoicePlayerButton disabled={!voice}>
                    <ImageWrapper>
                        <PreviewImage src={StopPreview} />
                    </ImageWrapper>
                </VoicePlayerButton>
            </CustomTooltip>
        );
    }

    return (
        <CustomTooltip title={tooltipText} placement="top" onClick={onPlay} disabled={listenDisabled} cursor="pointer">
            <VoicePlayerButton aria-label="play preview" disabled={listenDisabled}>
                <ImageWrapper>
                    <PreviewImage src={PlayPreview} />
                </ImageWrapper>
            </VoicePlayerButton>
        </CustomTooltip>
    );
});
